import { Injectable } from "@angular/core";
import { HelperService } from "src/app/shared/helpers/helper.service";
import {
    Mesh,
    Vector2,
    Vector3,
    Box3,
    PerspectiveCamera,
    Raycaster,
    Object3D,
    Camera,
    Matrix3,
    Intersection,
} from "three";
import { AlteredShapeConfiguration, Object3DUserData } from "@utils/shape";
import { SetShapeUserDataService } from "src/app/shared/helpers/set-shape-userdata.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Face, FaceType } from "../../../../../utils/count-calculator/count-calculator.model";
import { CanvasConfig } from "../../../../../utils/canvas-configuration";
import { FaceIdentifierService } from "../../shared/face-identifier/face-identifier.service";
import { CubeFace } from "../../shared/face-identifier/face-identifier.constants";
import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import { MatSnackBarMessages } from "../../shared/mat-snackbar/mat-snackbar.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { ActivatedRoute } from "@angular/router";
import { CellReference } from "@models/mesh.model";
import { CopyPasteService } from "./copypaste.service";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { SceneManagerService } from "../scene-manager/scene-manager.service";
import { UndoRedoService } from "../undoRedo/undo-redo.service";
import { Action, UndoRedoAction } from "../../shared/models/undoRedo.model";
import { ComponentInteractionService } from "../component-interaction/component-interaction.service";
import { MeshVisualService } from "../designer/mesh-visual.service";

@Injectable({
    providedIn: "root",
})
export class DragAndDropService {
    public draggedMesh: Mesh = new Mesh();
    private meshes: Mesh[] = [];
    private selectedMeshes: Mesh[] = [];
    private groundPositionY =
        CanvasConfig.planePostionIn_Y_Coordinate +
        AlteredShapeConfiguration.standardHeigthOfShape / AlteredShapeConfiguration.divisorAdjustYPosition;
    private isFromProductDetail: boolean = false;
    private defaultScaleOfAnyShape = new Vector3(1, 1, 1);
    private firstIntersection: Intersection<Object3D> | null = null;
    private dropOccurredSubject = new Subject<boolean>();
    public dropOccurred$ = this.dropOccurredSubject.asObservable();

    // Reusable objects for calculations
    private reusableVector = new Vector3();
    private draggedBox = new Box3();
    private meshBox = new Box3();

    constructor(
        private helperService: HelperService,
        private setShapeUserDataService: SetShapeUserDataService,
        private snackBar: MatSnackBar,
        private faceIdentifier: FaceIdentifierService,
        private copyPasteService: CopyPasteService,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private sceneManagerService: SceneManagerService,
        private undoRedoService: UndoRedoService,
        private componentInteractionService: ComponentInteractionService,
        private meshVisualService: MeshVisualService,
    ) {
        window.addEventListener("keydown", this.copyPasteService.handleKeydown.bind(this.copyPasteService));
        window.addEventListener("click", (event) => {
            this.copyPasteService.clickMouseEvent = event;
        });
        this.route.queryParams.subscribe((params) => {
            this.isFromProductDetail = !!params["fromProductDetail"];
        });

        this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
        });

        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            this.selectedMeshes = selections.map((selection) => selection.mesh);
        });
    }

    /**
     * Clears the cell reference from the object's user data.
     */
    private clearCellReference(object: Object3D) {
        if (object.userData?.[Object3DUserData.cellReference]) {
            object.userData[Object3DUserData.cellReference] = null;
        }
    }

    /**
     * Handles the dragging of the selected shape and collision detection.
     *
     * @param draggableMesh - The mesh being dragged.
     * @param mouseMovePosition - The current mouse position in normalized device coordinates.
     * @param camera - The active camera for raycasting.
     * @param planeMesh - The plane mesh
     */
    public async dragMesh(
        draggableMesh: Mesh,
        mouseMovePosition: Vector2,
        camera: Camera,
        planeMesh: Mesh,
    ): Promise<void> {
        if (this.isFromProductDetail) {
            return;
        }
        this.draggedMesh = draggableMesh;
        this.clearCellReference(this.draggedMesh);

        const raycaster = new Raycaster();
        raycaster?.setFromCamera(mouseMovePosition, camera);
        const interactions = raycaster.intersectObjects([planeMesh, ...this.meshes], false);

        this.firstIntersection = interactions[0];
        if (!this.firstIntersection) {
            return;
        }

        const planeIntersection = interactions.find(
            (interaction) => interaction.object.name === AuxiliaryObjectType.Plane,
        );

        await this.handleDragMovement(planeIntersection);

        this.adjustYForScaledShapes(this.draggedMesh);
    }

    /**
     * Handles core movement logic when dragging.
     *
     * @param targetPoint - The target position for the dragged object.
     */
    private async handleDragMovement(planeIntersection?: Intersection): Promise<void> {
        const collidedMeshes = await this.detectCollisions();

        if (collidedMeshes.length) {
            await this.handleCollisionMovement(collidedMeshes, planeIntersection);
        } else {
            if (!planeIntersection) return;
            await this.handleNormalMovement(planeIntersection);
        }
    }

    /**
     * Detects collisions between the dragged mesh and other meshes in the scene.
     *
     * - Expands the bounding box of the dragged mesh to prevent precision issues.
     * - Filters out the dragged mesh and the ground plane.
     * - Calculates bounding boxes for other meshes and checks for intersections.
     *
     * @returns A promise resolving to an array of colliding meshes.
     */
    private async detectCollisions(): Promise<Mesh[]> {
        // Calculate dragged mesh bounding box once and store it
        this.calculateBoundingBox(this.draggedMesh, this.draggedBox, this.reusableVector);
        this.draggedBox.expandByScalar(0.1);

        return this.meshes.filter((mesh) => {
            if (mesh === this.draggedMesh || mesh.name === AuxiliaryObjectType.Plane) {
                return false;
            }

            // Calculate current mesh's bounding box
            this.calculateBoundingBox(mesh, this.meshBox, this.reusableVector);
            this.meshBox.expandByScalar(0.1);

            return this.draggedBox.intersectsBox(this.meshBox);
        });
    }

    /**
     * Computes and sets the bounding box of a given mesh.
     *
     * - If the mesh is a special type (e.g., HalfCylinder or HemiSphere), it constructs
     *   a cubic bounding box based on the geometry's size.
     * - Otherwise, it derives the bounding box directly from the mesh.
     *
     * @param mesh - The mesh to compute the bounding box for.
     * @param boundingBox - The bounding box to be updated.
     * @param reusableVector - A reusable vector to minimize object creation.
     */
    private calculateBoundingBox(mesh: Mesh, boundingBox: Box3, reusableVector: Vector3): void {
        if (this.isSpecialMesh(mesh)) {
            const geometry = mesh.geometry;
            const boundingSize = geometry.boundingBox!.getSize(reusableVector);
            const maxDimension = Math.max(boundingSize.x, boundingSize.y, boundingSize.z);

            boundingBox.setFromCenterAndSize(
                mesh.position,
                reusableVector.set(maxDimension, maxDimension, maxDimension),
            );
        } else {
            boundingBox.setFromObject(mesh);
        }
    }

    /**
     * Checks if a given mesh is a special type that requires a cubic bounding box.
     *
     * @param mesh - The mesh to check.
     * @returns True if the mesh is a HalfCylinder or HemiSphere, otherwise false.
     */
    private isSpecialMesh(mesh: Mesh): boolean {
        return mesh.name === MeshType.HalfCylinder || mesh.name === MeshType.HemiSphere;
    }

    /**
     * Validates and processes movement based on the intersection point.
     * - Detects the face type at the intersection point.
     * - Prevents placement on curved faces, displaying an error message if attempted.
     * - Checks if the grid is already occupied at the intersection point.
     * - Displays an error message if placement is not allowed.
     *
     * @param intersectionPoint - The intersection point where movement is being validated.
     * @returns A promise resolving to an object containing:
     *          - `isValid`: Whether the placement is valid.
     *          - `face`: The detected face type at the intersection point.
     */
    private async validateAndHandleMovement(
        intersectionPoint: Intersection,
    ): Promise<{ isValid: boolean; face: Face | FaceType | null }> {
        // Perform face detection first
        const face = this.worldFaceDetection(intersectionPoint);

        if (face === null) {
            return { isValid: false, face: null };
        }

        // Check for curved face immediately
        if (face === FaceType.Curved) {
            this.snackBar.open(
                this.getTranslation("designer.invalidMessage.cannotPlace"),
                SnackBarConfig.Actions.CLOSE,
                {
                    duration: SnackBarConfig.Duration.SHORT,
                },
            );
            return { isValid: false, face };
        }

        // Check grid occupation
        const isGridOccupied = await this.checkIsGridOccupied(intersectionPoint, face);
        if (isGridOccupied) {
            this.snackBar.open(
                this.getTranslation("designer.invalidMessage.gridUnavailable"),
                SnackBarConfig.Actions.CLOSE,
                {
                    duration: SnackBarConfig.Duration.SHORT,
                },
            );
            return { isValid: false, face };
        }

        return { isValid: true, face };
    }

    /**
     * Handles movement when no collision is detected.
     * - Reverts any previous collision adjustments.
     * - Moves the dragged mesh if it intersects the plane or itself.
     * - Prevents placement on curved faces and occupied grid areas.
     * - Snaps the mesh to valid faces when applicable.
     *
     * @param planeIntersectionPoint - Target point for movement.
     */
    private async handleNormalMovement(planeIntersection: Intersection): Promise<void> {
        this.revertCollisionActions();
        this.draggedMesh.position.y = this.groundPositionY;

        if (!this.firstIntersection) {
            return;
        }

        if (
            this.firstIntersection.object === this.draggedMesh ||
            this.firstIntersection.object.name === AuxiliaryObjectType.Plane
        ) {
            const { isValid, face } = await this.validateAndHandleMovement(planeIntersection);
            if (!isValid || face === null) {
                return;
            }
            this.moveDraggedMesh(planeIntersection.point);
        } else {
            const { isValid, face } = await this.validateAndHandleMovement(this.firstIntersection);
            if (!isValid || face === null) {
                return;
            }
            this.snapMeshOnFaces(this.firstIntersection.object as Mesh, face);
        }
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    /**
     * Handles collision actions when the dragged mesh intersects with objects.
     * - Performs necessary collision adjustments.
     * - Checks if the placement area is occupied or invalid.
     * - Moves or snaps the mesh if placement is valid.
     *
     * @param collidedMeshes - Meshes the dragged object collided with.
     * @param planeIntersectionPoint - Target position on the plane.
     */
    private async handleCollisionMovement(collidedMeshes: Mesh[], planeIntersection?: Intersection): Promise<void> {
        if (!this.firstIntersection) {
            return;
        }

        collidedMeshes.forEach(() => {
            this.performCollisionActions(this.draggedMesh);
        });

        if (this.firstIntersection.object.name === AuxiliaryObjectType.Plane) {
            if (!planeIntersection) return;
            const { isValid, face } = await this.validateAndHandleMovement(this.firstIntersection);
            if (!isValid || face === null) {
                return;
            }

            const wouldCollide = await this.checkCollisionAfterMove(planeIntersection.point);
            if (!wouldCollide) {
                await this.handleNormalMovement(planeIntersection);
            }
        } else if (this.firstIntersection.object === this.draggedMesh) {
            // Handle self-intersection case if needed
        } else {
            const { isValid, face } = await this.validateAndHandleMovement(this.firstIntersection);
            if (!isValid || face === null) {
                return;
            }

            this.snapMeshOnFaces(this.firstIntersection.object as Mesh, face);
        }
    }

    /**
     * Checks if moving the dragged mesh to the given position would result in a collision
     *
     * @param newPosition - The potential new position for the dragged mesh
     * @returns True if collision would occur, false otherwise
     */
    private async checkCollisionAfterMove(newPosition: Vector3): Promise<boolean> {
        // Store the original position
        const originalPosition = this.draggedMesh.position.clone();
        // Temporarily move the mesh to check collision
        this.draggedMesh.position.copy(newPosition);
        // Run collision detection
        const collidingMeshes = await this.detectCollisions();
        // Restore the original position
        this.draggedMesh.position.copy(originalPosition);
        // Return true if there are any collisions
        return collidingMeshes.length > 0;
    }

    /**
     * Checks whether a given position is within the allowed canvas boundaries.
     *
     * @param position - The position to validate.
     * @returns True if the position is within the valid range, otherwise false.
     */
    private isValidPosition(position: Vector3): boolean {
        return (
            position.x <= CanvasConfig.maxMinConstant &&
            position.x >= -CanvasConfig.maxMinConstant &&
            position.z >= -CanvasConfig.maxMinConstant &&
            position.z <= CanvasConfig.maxMinConstant
        );
    }

    /**
     * Snaps the dragged mesh onto a specific face of the found mesh, ensuring alignment based on face normals.
     * Prevents placement on curved surfaces and adjusts the position according to the target face.
     *
     * @param foundMesh - The mesh to snap onto.
     * @param face - The face of the found mesh where the dragged mesh should be snapped.
     */
    private snapMeshOnFaces(foundMesh: Mesh, face: string): void {
        const realMeshFace = this.faceIdentifier.identifyFace(this.firstIntersection!.face!.normal);
        const faces = CubeFace;
        const foundFace = Object.entries(faces).filter(([key, value]) => value === realMeshFace);
        const faceKeyName = foundFace.length > 0 ? foundFace[0][0] : undefined;

        if (!faceKeyName || faceKeyName === FaceType.Curved) {
            this.snackBar.open(
                this.getTranslation("designer.invalidMessage.cannotPlace"),
                SnackBarConfig.Actions.CLOSE,
                {
                    duration: SnackBarConfig.Duration.SHORT,
                },
            );
            return;
        }

        // Dragable Mesh will Show according to face normal
        const offset = AlteredShapeConfiguration.standardHeigthOfShape;
        const nearestGridCords = this.helperService.getNearestGridCords(
            new Vector2(this.draggedMesh.position.x, this.draggedMesh.position.z),
        );

        const targetPosition = new Vector3(foundMesh.position.x, foundMesh.position.y, foundMesh.position.z);

        // Adjust position based on face type
        switch (face) {
            case Face.Top:
                targetPosition.y += offset; // Example adjustment for the top face
                break;
            case Face.Bottom:
                targetPosition.y -= offset; // Adjustment for the bottom face
                break;
            case Face.Front:
                targetPosition.x += offset; // Adjustment for the front face
                break;
            case Face.Back:
                targetPosition.x -= offset; // Adjustment for the back face
                break;
            case Face.Left:
                targetPosition.z += offset; // Adjustment for the left face
                break;
            case Face.Right:
                targetPosition.z -= offset; // Adjustment for the right face
                break;
            default:
                this.draggedMesh.position.set(nearestGridCords.x, foundMesh.position.y, nearestGridCords.y);
                break;
        }

        if (this.isValidPosition(targetPosition)) {
            this.draggedMesh.position.copy(targetPosition);
        }
    }

    /**
     * Moves the dragged mesh to a new position if it is within the valid boundaries.
     *
     * @param point - The target position to move the dragged mesh to.
     * @returns Resolves when the movement is completed.
     */
    private async moveDraggedMesh(point: Vector3): Promise<void> {
        if (this.isValidPosition(point)) {
            this.draggedMesh.position.set(point.x, this.draggedMesh.position.y, point.z);
        }
    }

    /**
     * Reverts any temporary changes applied to the dragged mesh during collision detection.
     * This resets the mesh's collision state, material, and vertical position.
     */
    private revertCollisionActions(): void {
        this.draggedMesh.userData[Object3DUserData.isInCollision] = false;
        const isMeshSelected = this.selectedMeshes.includes(this.draggedMesh);
        this.meshVisualService.removeCollisionHighlight(this.draggedMesh, isMeshSelected);
    }

    /**
     * Determines if the grid cell corresponding to the given intersection point is occupied.
     *
     * @param intersection - The intersection point where a mesh or plane was hit.
     * @param activeFace - (Optional) The face of the shape that is being checked for adjacency.
     * @returns Resolves to `true` if the grid cell is occupied, otherwise `false`.
     */
    private async checkIsGridOccupied(
        intersection: Intersection<Object3D>,
        activeFace?: Face | FaceType,
    ): Promise<boolean> {
        if (!intersection) {
            return false;
        }

        if (intersection.object.name === AuxiliaryObjectType.Plane) {
            const cellSize = CanvasConfig.cellSize;
            const { x, z } = intersection.point;
            const nearestGridCoords = this.helperService.getNearestGridCords(new Vector2(x, z));
            const cellReference = {
                row: this.calculateGridIndex(nearestGridCoords.y, cellSize),
                column: this.calculateGridIndex(nearestGridCoords.x, cellSize),
                cell: 0,
            };
            return this.isMeshInCellReference(cellReference);
        }

        const cellReference = intersection.object.userData[Object3DUserData.cellReference];
        const adjacentCellReference = this.getAdjacentCellForFace(activeFace!, cellReference);

        return adjacentCellReference ? this.isMeshInCellReference(adjacentCellReference) : false;
    }

    /**
     * Determines the adjacent grid cell reference based on the given face direction.
     *
     * @param face - The face of the mesh being checked.
     * @param currentCellReference - The current cell reference containing row, column, and cell values.
     * @returns The updated cell reference of the adjacent grid position.
     */
    private getAdjacentCellForFace(face: string, currentCellReference: CellReference): CellReference {
        let adjacentCellReference = { ...currentCellReference };
        switch (face) {
            case Face.Front:
                adjacentCellReference.column += 1;
                break;
            case Face.Right:
                adjacentCellReference.row -= 1;
                break;
            case Face.Back:
                adjacentCellReference.column -= 1;
                break;
            case Face.Left:
                adjacentCellReference.row += 1;
                break;
            case Face.Top:
                adjacentCellReference.cell += 1;
                break;
            case Face.Bottom:
                adjacentCellReference.cell -= 1;
                break;
        }
        return adjacentCellReference;
    }

    /**
     * Calculates the grid index for a given coordinate based on cell size.
     *
     * @param coordinate - The x or z coordinate.
     * @param cellSize - The size of a single grid cell.
     * @returns The calculated grid index.
     */
    private calculateGridIndex(coordinate: number, cellSize: number): number {
        return coordinate / cellSize;
    }

    /**
     * Checks if a mesh exists in the specified grid cell reference.
     *
     * @param cellReference - The grid cell reference containing row, clumn, and cell values.
     * @returns `true` if a mesh is found in the given cell, otherwise `false`.
     */
    private isMeshInCellReference(cellReference: CellReference): boolean {
        return this.meshes.some(
            (mesh) =>
                mesh.userData?.[Object3DUserData.cellReference] &&
                mesh.userData[Object3DUserData.cellReference].row === cellReference.row &&
                mesh.userData[Object3DUserData.cellReference].column === cellReference.column &&
                mesh.userData[Object3DUserData.cellReference].cell === cellReference.cell,
        );
    }

    /**
     * Detects the world-space face direction of the intersection.
     *
     * @param intersection - The intersection data from raycasting.
     * @returns The detected face (Front, Back, Left, Right, Top, Bottom, or Curved).
     */
    private worldFaceDetection(intersection: Intersection<Object3D>): Face | FaceType | null {
        if (!intersection?.face) return null;

        const worldNormal = intersection.face.normal
            .clone()
            .applyMatrix3(new Matrix3().getNormalMatrix(intersection.object.matrixWorld))
            .normalize();

        if (Math.abs(worldNormal.x - 1) < 0.1) return Face.Front;
        if (Math.abs(worldNormal.x + 1) < 0.1) return Face.Back;
        if (Math.abs(worldNormal.y - 1) < 0.1) return Face.Top;
        if (Math.abs(worldNormal.y + 1) < 0.1) return Face.Bottom;
        if (Math.abs(worldNormal.z - 1) < 0.1) return Face.Left;
        if (Math.abs(worldNormal.z + 1) < 0.1) return Face.Right;

        return FaceType.Curved;
    }

    /**
     * Handles the dropping of a dragged mesh, updating its position in the grid, resolving collisions,
     * and ensuring proper placement in the scene.
     *
     * @param draggedMesh - The mesh object that is currently being dropped.
     * @param objects - The list of all objects in the scene, used for collision detection.
     * @returns The dropped mesh with its updated position.
     */
    public dropMesh(draggedMesh: Mesh): Mesh {
        if (this.isFromProductDetail) {
            return draggedMesh;
        }
        this.draggedMesh = draggedMesh;

        if (draggedMesh) {
            if (draggedMesh.userData[Object3DUserData.isInCollision]) {
                this.revertCollisionActions();
            } else {
                this.setShapeToNearGridCoords();
            }

            // Below lines will update the dragged object new position in the grid data.
            const { row, column, cell } = this.helperService.calculateRowAndColumn(this.draggedMesh as Mesh);

            this.draggedMesh.userData[Object3DUserData.cellReference] = {
                row: row,
                column: column,
                cell: cell,
            };
            this.updateInitialPosition();
        }

        this.setShapeUserDataService.adjacentData(this.meshes, draggedMesh);
        this.adjustYForScaledShapes(draggedMesh);
        this.dropOccurredSubject.next(true);
        return draggedMesh;
    }

    /**
     * Checks if the dragged shape has been scaled.
     *
     * This method compares the current scale of the dragged mesh with its default scale.
     * If they are not equal, it indicates that the shape has been resized.
     *
     * @returns `true` if the shape is scaled, otherwise `false`.
     */
    private get isShapeScaled(): boolean {
        return !this.draggedMesh.scale.equals(this.defaultScaleOfAnyShape);
    }

    /**
     * Adjusts the Y position of a scaled shape to maintain alignment with the plane.
     *
     * When a shape is scaled, it expands in all directions, including the Y-axis.
     * Since the Y position must remain constant to align with the plane, this method
     * manually adjusts the Y value based on the shape's scale.
     *
     * @param draggableMesh - The shape being adjusted.
     */
    private adjustYForScaledShapes(draggableMesh: Mesh): void {
        if (draggableMesh && this.isShapeScaled && draggableMesh.position) {
            draggableMesh.position.y =
                draggableMesh?.position.y +
                (AlteredShapeConfiguration.standardHeigthOfShape / 2) *
                    draggableMesh.userData[Object3DUserData.scaleCount];
        }
    }

    /**
     * Handles collision detection by updating the dragged shape's state and appearance
     * when it collides with another object.
     *
     * - Marks the shape as being in collision.
     * - Stores the ID of the collided object for reference.
     * - Applies a transparent red material to visually indicate the collision.
     * - Snaps the dragged shape to the nearest grid coordinates.
     *
     * @param draggedMesh - The shape currently being dragged.
     * @param collidedObject - The object that the dragged shape has collided with.
     */
    private performCollisionActions(draggedMesh: Mesh): void {
        draggedMesh.userData[Object3DUserData.isInCollision] = true;

        // Apply the new collision material to dragged mesh
        this.meshVisualService.setCollisionHighlight(draggedMesh);

        const nearestGridCords = this.helperService.getNearestGridCords(
            new Vector2(draggedMesh.position.x, draggedMesh.position.z),
        );

        draggedMesh.position.set(nearestGridCords.x, draggedMesh.position.y, nearestGridCords.y);
    }

    /**
     * Adjusts the dragged shape's position to align with the nearest grid.
     * Ensures proper placement on the ground or on top of another shape.
     */
    private setShapeToNearGridCoords(): void {
        const meshPosition = this.draggedMesh.position;
        meshPosition.y = this.helperService.getGroundLocationY();

        // First we get the nearest grid co-ordinates
        const nearestGridCords = this.helperService.getNearestGridCords(new Vector2(meshPosition.x, meshPosition.z));

        const snappedPosition = new Vector3(nearestGridCords.x, this.groundPositionY, nearestGridCords.y);

        this.draggedMesh.position.copy(snappedPosition);
    }

    /**
     * Updates the initial position of the dragged mesh to its current position.
     */
    private updateInitialPosition(): void {
        this.draggedMesh.userData[Object3DUserData.initialPosition].copy(this.draggedMesh.position);
    }

    /*
     * This method handels the dragging of the multiple shapes and collision detection.
     */
    public dragMultipleMeshes(
        draggableMeshes: Mesh[],
        mouseMovePosition: Vector2,
        camera: Camera,
        planeMesh: Mesh,
    ): void {
        if (!draggableMeshes || draggableMeshes.length === 0) return;
        this.captureInitialPositions(draggableMeshes);
        // Assume the first object is the reference initially
        let referenceObject = draggableMeshes[0];

        // Find the object with the lowest cell value in the same row and column
        for (const mesh of draggableMeshes) {
            if (
                mesh.userData[Object3DUserData.cellReference] &&
                mesh.userData[Object3DUserData.cellReference].cell <
                    referenceObject.userData[Object3DUserData.cellReference].cell
            ) {
                referenceObject = mesh;
            }
        }

        const initialOffsets: {
            object: Object3D;
            offset: Vector3;
        }[] = [];

        const meshes = this.meshes.filter(
            (mesh) => !draggableMeshes.some((draggable) => draggable.uuid === mesh.uuid) && mesh.name,
        );

        const raycaster = new Raycaster();
        raycaster?.setFromCamera(mouseMovePosition, camera);
        this.firstIntersection = raycaster.intersectObjects([planeMesh, ...meshes], false)[0];
        if (!this.firstIntersection) return;

        // Calculate and store relative offsets
        draggableMeshes.forEach((draggableObject) => {
            const offset = draggableObject.position.clone().sub(referenceObject.position);
            initialOffsets.push({ object: draggableObject, offset });
        });

        // Perform raycasting
        raycaster.setFromCamera(mouseMovePosition, camera);
        const intersects = raycaster.intersectObjects([planeMesh, ...meshes], false);
        if (intersects.length > 0) {
            const targetPoint = intersects[0].point;
            let posY = 0.5;
            if (
                this.firstIntersection.object.name &&
                this.firstIntersection.object.name !== AuxiliaryObjectType.Plane
            ) {
                posY = this.getTopMostY(this.firstIntersection.object as Mesh, meshes);
            }
            // Calculate the new position for the reference object
            const referenceNewPosition = new Vector3(targetPoint.x, posY, targetPoint.z);

            const previousPositions = new Map<Object3D, Vector3>(); // Store last valid positions

            // Create a single bounding box that includes all dragged objects
            const combinedBoundingBox = new Box3();
            draggableMeshes.forEach((draggableObject) => {
                combinedBoundingBox.expandByObject(draggableObject);
                previousPositions.set(draggableObject, draggableObject.position.clone()); // Save last position
            });

            // Move all draggable objects while maintaining their relative offsets
            draggableMeshes.forEach((draggableObject) => {
                const offset = initialOffsets.find((o) => o.object === draggableObject)?.offset || new Vector3();
                draggableObject.position.copy(referenceNewPosition.clone().add(offset));
            });

            // Call `checkCollision` **after moving the objects**
            const collisionDetected = this.checkCollision(draggableMeshes, meshes);

            if (collisionDetected) {
                //  Revert to previous positions to prevent objects from going inside other shapes
                draggableMeshes.forEach((draggableObject) => {
                    const lastValidPosition = previousPositions.get(draggableObject);
                    if (lastValidPosition) {
                        draggableObject.position.copy(lastValidPosition);
                    }
                });

                this.performMultipleCollisionActions(draggableMeshes as Mesh[]);
                // Handle collision case (e.g., revert or snap to grid)
            } else {
                this.revertCollisionActionsMultiple(draggableMeshes);
            }
        }
    }

    private getTopMostY(firstIntersectionMesh: Mesh, meshes: Mesh[]): number {
        meshes = meshes.filter((obj) => obj.name !== AuxiliaryObjectType.Plane);

        // Ensure the condition applies only when column exists
        const matchedItem = meshes
            .filter(
                (item) =>
                    item.userData[Object3DUserData.cellReference]?.column !== undefined && // Check if column is defined
                    item.userData[Object3DUserData.cellReference]?.column ===
                        firstIntersectionMesh.userData[Object3DUserData.cellReference]?.column &&
                    item.userData[Object3DUserData.cellReference]?.row ===
                        firstIntersectionMesh.userData[Object3DUserData.cellReference]?.row,
            )
            .reduce(
                (maxItem: Mesh | null, currentItem: Mesh) =>
                    !maxItem ||
                    currentItem.userData[Object3DUserData.cellReference].cell >
                        maxItem.userData[Object3DUserData.cellReference].cell
                        ? currentItem
                        : maxItem,
                null,
            );

        if (!matchedItem) {
            return firstIntersectionMesh.position.y + AlteredShapeConfiguration.positionYForMultipleDrag; // Use a fallback value
        }
        const y = matchedItem.position.y + 20;
        return y;
    }

    private checkCollision(draggableMeshes: Object3D[], meshes: Mesh[]): boolean {
        const combinedBoundingBox = new Box3();

        draggableMeshes.forEach((draggableObject) => {
            combinedBoundingBox.expandByObject(draggableObject);
        });

        let collisionDetected = false;

        meshes.forEach((mesh) => {
            if (
                draggableMeshes.includes(mesh) ||
                !mesh.visible ||
                !(mesh instanceof Mesh) ||
                mesh.name === AuxiliaryObjectType.Plane ||
                mesh.name === AuxiliaryObjectType.GridHelper
            )
                return;

            //   const objectBoundingBox = new Box3().setFromObject(object);
            this.calculateBoundingBox(mesh, this.meshBox, this.reusableVector);

            if (combinedBoundingBox.intersectsBox(this.meshBox)) {
                collisionDetected = true;
            }
        });
        return collisionDetected;
    }

    /*
     * This method handels the multiple shapes collision detection, we are currently reducing the opacity of the dragged shape if it comes in
     * contact with any of the shape on the plane. And these changes are temporary.
     */
    private performMultipleCollisionActions(draggedObjects: Mesh[]): void {
        const combinedBoundingBox = new Box3();

        for (let i = 0; i < draggedObjects.length; i++) {
            const draggedObject = draggedObjects[i];

            // Expand the combinedBoundingBox to include each dragged object
            combinedBoundingBox.expandByObject(draggedObject);

            draggedObject.userData[Object3DUserData.isInCollision] = true;

            // Apply the new transparent material to each dragged mesh
            this.meshVisualService.setCollisionHighlight(draggedObject);

            // Get the nearest grid coordinates for each dragged object
            const nearestGridCords = this.helperService.getNearestGridCords(
                new Vector2(draggedObject.position.x, draggedObject.position.z),
            );

            draggedObject.position.set(nearestGridCords.x, draggedObject.position.y, nearestGridCords.y);
        }
    }

    /*
     Reverts back all the temporary changes done on the multiple shapes during collision.
     */
    private revertCollisionActionsMultiple(draggedObjects: Mesh[]): void {
        draggedObjects.forEach((draggedObject) => {
            draggedObject.userData[Object3DUserData.isInCollision] = false;
            const isMeshSelected = this.selectedMeshes.includes(draggedObject);
            this.meshVisualService.removeCollisionHighlight(draggedObject, isMeshSelected);
        });
    }

    // store initial positions
    private objectInitialPositions: Map<string, Vector3> = new Map();

    // store initial positions before any drag operation
    public captureInitialPositions(draggableMeshes: Mesh[]): void {
        draggableMeshes.forEach((mesh) => {
            // Only store if not already stored for this object
            if (!this.objectInitialPositions.has(mesh.uuid)) {
                this.objectInitialPositions.set(mesh.uuid, mesh.position.clone());
            }
        });
    }

    // restore meshes to their original positions AND update metadata
    private restoreAndDropAtOriginalPositions(meshes: Mesh[]): Object3D[] {
        meshes.forEach((mesh) => {
            const originalPosition = this.objectInitialPositions.get(mesh.uuid);
            if (originalPosition) {
                // Set the mesh position back to original
                mesh.position.set(originalPosition.x, originalPosition.y, originalPosition.z);
            }
        });
        // Update appearance
        this.revertCollisionActionsMultiple(meshes);
        this.resetStoredPositions();
        this.dropOccurredSubject.next(true);
        return meshes;
    }

    //reset stored positions when needed
    public resetStoredPositions(): void {
        this.objectInitialPositions.clear();
    }

    public dropMultipleObjects(
        draggableMeshes: Mesh[],
        planeMesh: Mesh,
        event: MouseEvent,
        camera: PerspectiveCamera,
    ): Object3D[] | null {
        if (!draggableMeshes || draggableMeshes.length === 0) return null;
        // Remove selected draggable objects from the scene's objects list
        const meshes = this.meshes.filter(
            (mesh) =>
                !draggableMeshes.some((draggable) => draggable.uuid === mesh.uuid) && mesh.name && mesh instanceof Mesh,
        ) as Mesh[];

        let referenceObject = draggableMeshes[0];

        referenceObject = this.helperService.getReferenceMesh(referenceObject, draggableMeshes);

        // Mouse event to world coordinates
        const mouse = new Vector2(
            (event.clientX / window.innerWidth) * 2 - 1,
            -(event.clientY / window.innerHeight) * 2 + 1,
        );

        const raycaster = new Raycaster();
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects([...meshes, planeMesh], false);

        if (intersects.length === 0) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessage7, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        // **Check if the intersected face is curved**
        const firstIntersection = intersects[0];
        if (!firstIntersection.face) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessage6, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.SHORT,
            });
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        const realMeshFace = this.faceIdentifier.identifyFace(
            firstIntersection.face!.normal.applyQuaternion(firstIntersection.object.quaternion),
        );

        if (realMeshFace === CubeFace.Curved) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessageToPlaceCurved, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.SHORT,
            });
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        const location = new Vector3();
        const data = this.helperService.calculatePosition(intersects, location, true);
        const gridPosition = this.helperService.getNearestGridCords(new Vector2(data?.loc.x, data?.loc.z));

        if (!gridPosition) {
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        let newLocation = new Vector3(gridPosition.x, data?.loc.y, gridPosition.y);

        const offsets = draggableMeshes.map((mesh) => mesh.position.clone().sub(referenceObject.position));

        const newPositions: Vector3[] = offsets.map((offset) => {
            return newLocation.clone().add(offset);
        });

        // **Check Grid Bounds**
        const isOutOfGrid = newPositions.some(
            (pos) =>
                pos.x < -CanvasConfig.maxMinConstant ||
                pos.x > CanvasConfig.maxMinConstant ||
                pos.z < -CanvasConfig.maxMinConstant ||
                pos.z > CanvasConfig.maxMinConstant,
        );

        if (isOutOfGrid) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessage4, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        const isConnected = this.isSingleConnectedStructure(draggableMeshes);
        const areAllOnSamePlane = draggableMeshes.every(
            (mesh) => Math.abs(mesh.position.y - draggableMeshes[0].position.y) < 0.01,
        );

        const isDroppedOnPlane = firstIntersection?.object?.name === AuxiliaryObjectType.Plane;

        if ((!isConnected && !areAllOnSamePlane && !isDroppedOnPlane) || (!isConnected && !isDroppedOnPlane)) {
            this.snackBar.open(
                this.getTranslation("designer.invalidMessage.cannotPlaceDisconnectedStructure"),
                SnackBarConfig.Actions.CLOSE,
                {
                    duration: SnackBarConfig.Duration.MEDIUM,
                },
            );
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        // Check if any of the objects in new positions have space to drop
        let canDrop = newPositions.every((newPos) => {
            return !meshes.some((existingMesh) => {
                // let existingBox = new Box3().setFromObject(existingMesh);
                this.calculateBoundingBox(existingMesh, this.meshBox, this.reusableVector);
                return this.meshBox.containsPoint(newPos); // Check if the space is occupied
            });
        });

        if (!canDrop) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessageForDrop, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.SHORT,
            });
            return this.restoreAndDropAtOriginalPositions(draggableMeshes);
        }

        // **Move Objects**
        for (let i = 0; i < draggableMeshes.length; i++) {
            const object = draggableMeshes[i];
            // Use the pre-calculated offset to place the object relative to new location
            object.position.copy(newLocation.clone().add(offsets[i]));

            // Ensure each object is precisely on a grid point
            const nearestGridCoords = this.helperService.getNearestGridCords(
                new Vector2(object.position.x, object.position.z),
            );
            object.position.set(nearestGridCoords.x, object.position.y, nearestGridCoords.y);
            this.dropOccurredSubject.next(true);
        }

        // **Update Metadata**
        draggableMeshes.forEach((draggedMesh) => {
            const { row, column, cell } = this.helperService.calculateRowAndColumn(draggedMesh as Mesh);

            draggedMesh.userData[Object3DUserData.cellReference] = {
                row: row,
                column: column,
                cell: cell,
            };
            this.updateInitialPositionMultiple(draggedMesh);
            this.setShapeUserDataService.adjacentData(this.meshes, draggedMesh);
        });

        // Update initial positions with the new successful positions
        draggableMeshes.forEach((mesh) => {
            this.objectInitialPositions.set(mesh.uuid, mesh.position.clone());
        });

        this.revertCollisionActionsMultiple(draggableMeshes);
        this.resetStoredPositions();
        return draggableMeshes;
    }

    private updateInitialPositionMultiple(draggedObject?: Mesh): void {
        const initialdrag = draggedObject ? draggedObject : this.draggedMesh;
        initialdrag.userData[Object3DUserData.initialPosition].copy(initialdrag.position);
    }

    private isSingleConnectedStructure(objects: Object3D[], tolerance: number = 0.1): boolean {
        if (objects.length <= 1) return true;

        const boundingBoxes = objects.map((obj) => {
            const box = new Box3().setFromObject(obj);
            box.min.subScalar(tolerance);
            box.max.addScalar(tolerance);
            return box;
        });

        const visited = new Set<number>();
        const queue: number[] = [0];
        visited.add(0);

        while (queue.length > 0) {
            const current = queue.shift()!;
            for (let i = 0; i < boundingBoxes.length; i++) {
                if (!visited.has(i) && boundingBoxes[current].intersectsBox(boundingBoxes[i])) {
                    visited.add(i);
                    queue.push(i);
                }
            }
        }

        return visited.size === objects.length;
    }
}
